// practiceLostRecord — the one core for the lost-practice record and the page flag (ai.346 DR1;
// DESIGN_REVIEW_2026-10-08_ai346-practice-switch-refusal.md L5-1, L5-4, L7-1, L6-2).
//
// When the server refuses the selected practice (403 PRACTICE_SWITCH_DENIED), the API interceptor (lib/api.ts)
// clears the selection and reloads. Two things outlive that moment, and both live here:
//
//  - THE RECORD (sessionStorage, survives the reload): { user_id, practice_id, name, at }. It is OWNED: the notice
//    (components/layout/PracticeLostBanner.tsx) shows it only to the user it names, only while it is fresh, and
//    removes it otherwise; sign-out and the forced logout remove it. The same record is the loop breaker (L7-1): the
//    same practice refused again for the same user soon after a refusal reload is held without another reload.
//    The key keeps its old name: a legacy bare-name value fails the parse and is removed silently.
//  - THE PAGE FLAG (module state, dies with the page): the lost practice's name, set just before the clear. While
//    it is set, the reload has not happened yet (cancelled at "Reload site?", or still pending): the request
//    interceptor lets nothing but /auth/* leave without a practice, and the notice says "Reload the page to
//    continue." There is no unset: a reload is the only reset.
//
// A pure module: no store, no React, so the shell can import it under any lib/api mock. Every storage call is in
// try/catch and never throws. Every comparison against the record is EXACT string equality. A practice name is
// not PHI.

const KEY = 'previsit_practice_lost';

/** L7-1 — the same practice refused again within this long of a refusal reload is a loop: hold, do not reload. */
export const PRACTICE_LOST_LOOP_WINDOW_MS = 60_000;
/** L5-1 — the post-reload notice is shown only while its record is at most this old. */
export const PRACTICE_LOST_NOTICE_TTL_MS = 600_000;
/** L6-2 — how long a refusal of the page's own practice holds its caller's rejection (the page is being replaced). */
export const PRACTICE_REFUSAL_HOLD_MS = 4_000;

export interface PracticeLostRecord {
  user_id: string;
  practice_id: string;
  name: string;
  at: number;
}

export function writePracticeLostRecord(record: PracticeLostRecord): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({
      user_id: record.user_id, practice_id: record.practice_id, name: record.name, at: record.at,
    }));
  } catch {
    /* storage unavailable (private mode / quota) — no record, so no notice and no breaker in the next page */
  }
}

/** The stored record; 'invalid' when a value is there but is not a record (unparseable, or any field of the wrong
 *  type: user_id / practice_id non-empty strings, name a string, at a finite number); null when there is none.
 *  VALIDITY is separate from DISPLAY (D-2026-10-15-04): an empty name ('', a nameless practice) is a valid record, so
 *  the L7-1 breaker holds for it too; the notice words it without a name. */
export function readPracticeLostRecord(): PracticeLostRecord | 'invalid' | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const r = JSON.parse(raw);
    if (r && typeof r === 'object'
      && typeof r.user_id === 'string' && r.user_id !== ''
      && typeof r.practice_id === 'string' && r.practice_id !== ''
      && typeof r.name === 'string'
      && typeof r.at === 'number' && Number.isFinite(r.at)) {
      return { user_id: r.user_id, practice_id: r.practice_id, name: r.name, at: r.at };
    }
  } catch {
    /* not JSON: a legacy bare name, or a damaged value */
  }
  return 'invalid';
}

export function removePracticeLostRecord(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

let reloadPendingName: string | null = null;

/** L5-4 — set by the refusal just before it clears the practice. Holds the lost practice's NAME so the notice can
 *  name it even when the record write failed; a missing name is held as '' and still counts as set. */
export function setPracticeReloadPending(name: string): void {
  reloadPendingName = typeof name === 'string' ? name : '';
}

/** The lost practice's name while the reload is pending, else null. "Set" means anything but null. */
export function getPracticeReloadPending(): string | null {
  return reloadPendingName;
}
